import { message } from 'antd'
import { useLingui } from '@lingui/react/macro'

import type { $Enums } from '@prisma/client'


import { useRef } from 'react';
import { api } from '@/utils/api';
interface UseReferenceUploadProps {
  onError?: () => void
  setUploading: (uploading: boolean) => void
  onSuccess?: (token: string) => void
  /** Cover 模式下，polling 成功后检测到歌词时回调 */
  onDetectedLyrics?: (lyrics: string) => void
}

export interface ReferenceUploadParams {
  uploadParams: {
    musicModel: Extract<$Enums.MusicModel, 'V2' | 'V3'>
    musicType: Extract<
      $Enums.MusicType,
      'Instrumental' | 'Song' | 'Vocal' | 'Cover' | 'Tts'
    >
    originFile?: string
  }
}

export function useReferenceUpload(props: UseReferenceUploadProps) {
  const { onError, setUploading, onSuccess, onDetectedLyrics } = props
  const { t } = useLingui()

  const refetchTime = 5 * 1000
  const timer = useRef<number | null>(null)

  const { mutateAsync: uploadUserReferenceTask } =
    api.reference.upload.useMutation()

  const { mutateAsync: pollingUserReferenceTask } =
    api.reference.polling.useMutation({
      onSuccess: (data, params) => {
        if (data.status === 'Pending') {
          if (timer.current) {
            clearTimeout(timer.current)
          }
          timer.current = window.setTimeout(() => {
            pollingUserReferenceTask(params)
          }, refetchTime)
        } else if (data.status === 'Failed') {
          message.error(data?.statusDesc || t`Upload failed`)
          setUploading(false)
          onDetectedLyrics?.('')
          onError?.()
        } else if (data.status === 'Success') {
          setUploading(false)
          // 始终回调 onDetectedLyrics（无歌词传空字符串以重置检测状态）
          if (onDetectedLyrics) {
            const lyrics =
              'detectedLyrics' in data && data.detectedLyrics
                ? (data.detectedLyrics as string)
                : ''
            onDetectedLyrics(lyrics)
          }
          onSuccess?.(params.token)
        }
      },
      onError: () => {
        setUploading(false)
        onDetectedLyrics?.('')
        onError?.()
      },
    })

  const handleReferenceUpload = async (
    props: ReferenceUploadParams['uploadParams'] & {
      file: string
      filename: string
      beginTime?: number
      endTime?: number
      isNeedDetectLyrics?: boolean
    },
  ) => {
    setUploading(true)
    const token = await uploadUserReferenceTask({
      ...props,
    })
    await pollingUserReferenceTask({
      token,
    })
    return token
  }

  const handleReferencePolling = async (token: string) => {
    setUploading(true)
    await pollingUserReferenceTask({
      token,
    })
    return token
  }

  return {
    handleReferenceUpload,
    handleReferencePolling,
  }
}
